"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.initStandardSuggestions = void 0;
const runtime_1 = require("@grafana/runtime");
const commands_1 = require("../utils/commands");
const types_1 = require("../types");
const language_1 = require("./language");
/**
 * This registry glues particular SuggestionKind with an async function that provides completion items for it.
 * To add a new suggestion kind, SQLEditor should be configured with a provider that implements customSuggestionKinds.
 */
const initStandardSuggestions = (functions, operators, macros) => () => [
    {
        id: types_1.SuggestionKind.SelectKeyword,
        name: types_1.SuggestionKind.SelectKeyword,
        suggestions: (_, m) => Promise.resolve([
            {
                label: `SELECT <column>`,
                insertText: `SELECT $0`,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Snippet,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
            },
            {
                label: `SELECT <column> FROM <table>`,
                insertText: `SELECT $2 FROM $1`,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Snippet,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.TemplateVariables,
        name: types_1.SuggestionKind.TemplateVariables,
        suggestions: (_, m) => {
            const templateSrv = (0, runtime_1.getTemplateSrv)();
            if (!templateSrv) {
                return Promise.resolve([]);
            }
            return Promise.resolve(templateSrv.getVariables().map((variable) => {
                const label = `\$${variable.name}`;
                const val = templateSrv.replace(label);
                return {
                    label,
                    detail: `(Template Variable) ${val}`,
                    kind: types_1.CompletionItemKind.Snippet,
                    documentation: `(Template Variable) ${val}`,
                    insertText: `\\$${variable.name} `,
                    insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                    command: commands_1.TRIGGER_SUGGEST,
                    sortText: types_1.CompletionItemPriority.Low,
                };
            }));
        },
    },
    {
        id: types_1.SuggestionKind.SelectMacro,
        name: types_1.SuggestionKind.SelectMacro,
        suggestions: (_, m) => Promise.resolve([
            ...macros
                .list()
                .filter((m) => m.type === types_1.MacroType.Value || m.type === types_1.MacroType.Column)
                .map(createMacroSuggestionItem),
        ]),
    },
    {
        id: types_1.SuggestionKind.TableMacro,
        name: types_1.SuggestionKind.TableMacro,
        suggestions: (_, m) => Promise.resolve([
            ...macros
                .list()
                .filter((m) => m.type === types_1.MacroType.Table)
                .map(createMacroSuggestionItem),
        ]),
    },
    {
        id: types_1.SuggestionKind.GroupMacro,
        name: types_1.SuggestionKind.GroupMacro,
        suggestions: (_, m) => Promise.resolve([
            ...macros
                .list()
                .filter((m) => m.type === types_1.MacroType.Group)
                .map(createMacroSuggestionItem),
        ]),
    },
    {
        id: types_1.SuggestionKind.FilterMacro,
        name: types_1.SuggestionKind.FilterMacro,
        suggestions: (_, m) => Promise.resolve([
            ...macros
                .list()
                .filter((m) => m.type === types_1.MacroType.Filter)
                .map(createMacroSuggestionItem),
        ]),
    },
    {
        id: types_1.SuggestionKind.WithKeyword,
        name: types_1.SuggestionKind.WithKeyword,
        suggestions: (_, m) => Promise.resolve([
            {
                label: `WITH <alias> AS ( ... )`,
                insertText: `WITH $1  AS ( $2 )`,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Snippet,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.StarWildCard,
        name: types_1.SuggestionKind.StarWildCard,
        suggestions: (_, m) => Promise.resolve([
            {
                label: '*',
                insertText: `* $0`,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Field,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.High,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.FunctionsWithArguments,
        name: types_1.SuggestionKind.FunctionsWithArguments,
        suggestions: (_, m) => Promise.resolve([
            ...functions.list().map((f) => ({
                label: f.name,
                insertText: `${f.name}($0)`,
                documentation: f.description,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Function,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumLow,
            })),
        ]),
    },
    {
        id: types_1.SuggestionKind.FunctionsWithoutArguments,
        name: types_1.SuggestionKind.FunctionsWithoutArguments,
        suggestions: (_, m) => Promise.resolve([
            ...functions.list().map((f) => ({
                label: f.name,
                insertText: `${f.name}()`,
                documentation: f.description,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Function,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumHigh,
            })),
        ]),
    },
    {
        id: types_1.SuggestionKind.FromKeyword,
        name: types_1.SuggestionKind.FromKeyword,
        suggestions: (_, m) => Promise.resolve([
            {
                label: 'FROM',
                insertText: `FROM $0`,
                command: commands_1.TRIGGER_SUGGEST,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
                kind: types_1.CompletionItemKind.Keyword,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.Schemas,
        name: types_1.SuggestionKind.Schemas,
        suggestions: (_, m) => Promise.resolve([]),
    },
    {
        id: types_1.SuggestionKind.Tables,
        name: types_1.SuggestionKind.Tables,
        suggestions: (_, m) => Promise.resolve([]),
    },
    {
        id: types_1.SuggestionKind.Columns,
        name: types_1.SuggestionKind.Columns,
        suggestions: (_, m) => Promise.resolve([]),
    },
    {
        id: types_1.SuggestionKind.LogicalOperators,
        name: types_1.SuggestionKind.LogicalOperators,
        suggestions: (_, m) => Promise.resolve(operators
            .list()
            .filter((o) => o.type === types_1.OperatorType.Logical)
            .map((o) => ({
            label: o.operator,
            insertText: `${o.operator} `,
            documentation: o.description,
            command: commands_1.TRIGGER_SUGGEST,
            sortText: types_1.CompletionItemPriority.MediumHigh,
            kind: types_1.CompletionItemKind.Operator,
        }))),
    },
    {
        id: types_1.SuggestionKind.WhereKeyword,
        name: types_1.SuggestionKind.WhereKeyword,
        suggestions: (_, m) => Promise.resolve([
            {
                label: 'WHERE',
                insertText: `WHERE `,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumHigh,
                kind: types_1.CompletionItemKind.Keyword,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.ComparisonOperators,
        name: types_1.SuggestionKind.ComparisonOperators,
        suggestions: (_, m) => Promise.resolve([
            ...operators
                .list()
                .filter((o) => o.type === types_1.OperatorType.Comparison)
                .map((o) => ({
                label: o.operator,
                insertText: `${o.operator} `,
                documentation: o.description,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumHigh,
                kind: types_1.CompletionItemKind.Operator,
            })),
            {
                label: 'IN (...)',
                insertText: `IN ( $0 )`,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
                kind: types_1.CompletionItemKind.Operator,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
            },
            {
                label: 'NOT IN (...)',
                insertText: `NOT IN ( $0 )`,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
                kind: types_1.CompletionItemKind.Operator,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
            },
            {
                label: 'IS',
                insertText: `IS`,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
                kind: types_1.CompletionItemKind.Operator,
            },
            {
                label: 'IS NOT',
                insertText: `IS NOT`,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
                kind: types_1.CompletionItemKind.Operator,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.GroupByKeywords,
        name: types_1.SuggestionKind.GroupByKeywords,
        suggestions: (_, m) => Promise.resolve([
            {
                label: 'GROUP BY',
                insertText: `GROUP BY `,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumHigh,
                kind: types_1.CompletionItemKind.Keyword,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.OrderByKeywords,
        name: types_1.SuggestionKind.OrderByKeywords,
        suggestions: (_, m) => Promise.resolve([
            {
                label: 'ORDER BY',
                insertText: `ORDER BY `,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.Medium,
                kind: types_1.CompletionItemKind.Keyword,
            },
            {
                label: 'ORDER BY(ascending)',
                insertText: `ORDER BY $1 ASC `,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumLow,
                kind: types_1.CompletionItemKind.Snippet,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
            },
            {
                label: 'ORDER BY(descending)',
                insertText: `ORDER BY $1 DESC`,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumLow,
                kind: types_1.CompletionItemKind.Snippet,
                insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.LimitKeyword,
        name: types_1.SuggestionKind.LimitKeyword,
        suggestions: (_, m) => Promise.resolve([
            {
                label: 'LIMIT',
                insertText: `LIMIT `,
                command: commands_1.TRIGGER_SUGGEST,
                sortText: types_1.CompletionItemPriority.MediumLow,
                kind: types_1.CompletionItemKind.Keyword,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.SortOrderDirectionKeyword,
        name: types_1.SuggestionKind.SortOrderDirectionKeyword,
        suggestions: (_, m) => Promise.resolve([language_1.ASC, language_1.DESC].map((o) => ({
            label: o,
            insertText: `${o} `,
            command: commands_1.TRIGGER_SUGGEST,
            kind: types_1.CompletionItemKind.Keyword,
        }))),
    },
    {
        id: types_1.SuggestionKind.NotKeyword,
        name: types_1.SuggestionKind.NotKeyword,
        suggestions: () => Promise.resolve([
            {
                label: 'NOT',
                insertText: 'NOT',
                command: commands_1.TRIGGER_SUGGEST,
                kind: types_1.CompletionItemKind.Keyword,
                sortText: types_1.CompletionItemPriority.High,
            },
        ]),
    },
    {
        id: types_1.SuggestionKind.BoolValues,
        name: types_1.SuggestionKind.BoolValues,
        suggestions: () => Promise.resolve(['TRUE', 'FALSE'].map((o) => ({
            label: o,
            insertText: `${o}`,
            command: commands_1.TRIGGER_SUGGEST,
            kind: types_1.CompletionItemKind.Keyword,
            sortText: types_1.CompletionItemPriority.Medium,
        }))),
    },
    {
        id: types_1.SuggestionKind.NullValue,
        name: types_1.SuggestionKind.NullValue,
        suggestions: () => Promise.resolve(['NULL'].map((o) => ({
            label: o,
            insertText: `${o}`,
            command: commands_1.TRIGGER_SUGGEST,
            kind: types_1.CompletionItemKind.Keyword,
            sortText: types_1.CompletionItemPriority.Low,
        }))),
    },
];
exports.initStandardSuggestions = initStandardSuggestions;
function createMacroSuggestionItem(m) {
    return {
        label: m.name,
        insertText: `${'\\' + m.text}${argsString(m.args)} `,
        insertTextRules: types_1.CompletionItemInsertTextRule.InsertAsSnippet,
        kind: types_1.CompletionItemKind.Snippet,
        documentation: m.description,
        command: commands_1.TRIGGER_SUGGEST,
    };
}
function argsString(args) {
    if (!args) {
        return '()';
    }
    return '('.concat(args.map((t, i) => `\${${i}:${t}}`).join(', ')).concat(')');
}
//# sourceMappingURL=standardSuggestionsRegistry.js.map